import type { JSX } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganization } from '@/features/organization/hooks';
import {
  useOrganizationProgress,
  useReconcileOrganizationProgress,
} from '@/features/memorization/hooks';
import { MembersSection } from '@/features/membership/members-section';
import { ProgressStatsGrid } from '@/features/memorization/progress-stats-grid';
import { ApiError } from '@/lib/api/client';
import { useHasCapability } from '@/lib/capabilities';

/**
 * Organization profile (Phase G-6). Read-only: `organizations:manage`
 * (the only capability that could unlock an edit form) is a Platform
 * Super Admin capability per docs/15 - ADR-020's "Rejected Alternatives"
 * explicitly rejected letting an Organization Admin update the profile -
 * so there is deliberately no edit affordance here, for any role this
 * screen renders for.
 */
export function OrganizationPage(): JSX.Element {
  const { data, isLoading, isError } = useOrganization();
  const canViewAggregateProgress = useHasCapability('memorization_targets:manage');
  const canReconcile = useHasCapability('settings:manage');
  const canManageTeachers = useHasCapability('teachers:manage');
  const canManageStudents = useHasCapability('students:manage');

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <h1 className="text-xl font-semibold">Organisasi</h1>

      {isLoading && (
        <Card>
          <CardContent className="flex flex-col gap-3 p-6">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-1/3" />
          </CardContent>
        </Card>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat profil organisasi.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && data !== undefined && (
        <Card>
          <CardHeader>
            <CardTitle>{data.name}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{data.organizationType}</Badge>
              <Badge variant={data.status === 'active' ? 'default' : 'outline'}>
                {data.status}
              </Badge>
            </div>
            <dl className="flex flex-col gap-2 text-sm">
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Slug</dt>
                <dd className="font-mono">{data.slug}</dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Email</dt>
                <dd>{data.email ?? '-'}</dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-muted-foreground">Telepon</dt>
                <dd>{data.phone ?? '-'}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}

      {canViewAggregateProgress && <OrganizationProgressCard canReconcile={canReconcile} />}

      {(canManageTeachers || canManageStudents) && <MembersSection />}
    </div>
  );
}

/**
 * Aggregate Progress across the whole organization (Phase G-6; ADR-019's
 * Addendum-to-the-Addendum). `memorization_targets:manage`-gated at the
 * backend, so this section only renders for a caller who actually holds
 * it (Teacher/Admin) - hiding it here is presentation only, the backend
 * still enforces the same gate on the request itself.
 */
function OrganizationProgressCard({
  canReconcile,
}: {
  readonly canReconcile: boolean;
}): JSX.Element {
  const { data, isLoading, isError } = useOrganizationProgress();
  const reconcile = useReconcileOrganizationProgress();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>Progress Organisasi</CardTitle>
        {canReconcile && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={reconcile.isPending}
            onClick={() => {
              reconcile.mutate();
            }}
          >
            {reconcile.isPending ? 'Menghitung Ulang...' : 'Hitung Ulang'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat progres organisasi.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && (
          <>
            <ProgressStatsGrid data={data} />
            <span className="text-xs text-muted-foreground">
              Terakhir direkonsiliasi:{' '}
              {data.lastReconciledAt !== null
                ? new Date(data.lastReconciledAt).toLocaleString('id-ID')
                : 'Belum pernah'}
            </span>
          </>
        )}

        {reconcile.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {reconcile.error instanceof ApiError
                ? reconcile.error.message
                : 'Gagal merekonsiliasi progres. Silakan coba lagi.'}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
